import { inject, Injectable } from '@angular/core';
import { EmitterService } from '../../../services/emitter.service';
import { AnnotationHighlightSelectedAnnotation } from '../../../interfaces';
import { AuthService } from '../../../services/auth.service';
import {
  onAddingAnnotationDataChanged,
  onAnnotationClickClosed,
  onAnnotationClickClosedV2,
  onAnnotationClickedIn,
  onAnnotationClickedOut,
  onAnnotationClickOpened,
  onAnnotationClickOpenedV2,
  onAnnotationHoveredIn,
  onAnnotationHoveredOut,
  onImageZoomChanged,
  onImageViewportChanged,
  onTimeFormatChangePlugin,
} from '../../../services/annotation.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { MediaType } from '../../../models/media-player-types';

/**
 * MTM-5990 - Annotation event listeners and event propagation
 * Service for managing annotation plugin event listener registration
 * Handles all plugin-to-service event propagation
 */
@Injectable()
export class AnnotationPluginEventService {
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly authService: AuthService = inject(AuthService);

  /**
   * Initialize plugin-specific annotation event listeners
   * Registers listeners on the annotation plugin that emit events to the annotation service
   */
  public init(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const annotationPlugin: any = store.annotationPluginSignal();

    if (!annotationPlugin) {
      return;
    }

    this.registerHighlightAndDataChangedListeners(annotationPlugin, store);
    this.registerTimeFormatListener(annotationPlugin);
    this.registerVideoClickListeners(annotationPlugin, store);
    this.registerImageAudioClickListeners(annotationPlugin, store);
    this.registerImageSpecificListeners(annotationPlugin, store);
  }

  /**
   * Register highlight annotation and data changed listeners
   */
  private registerHighlightAndDataChangedListeners(annotationPlugin: any, store: MediaPlayerStore): void {
    const instanceId: string = store.instanceIdSignal();
    const itemId: string = store.itemIdSignal();

    annotationPlugin.registerListener('highlightAnnotation', (event: any) => {
      if (event.detail.instanceId != instanceId || event.detail.itemId != itemId) {
        return;
      }
      EmitterService.get(AnnotationHighlightSelectedAnnotation).emit({
        comment: event.detail.annotation.comments[0],
        annotationId: event.detail.annotation.id,
        instanceId: instanceId,
        itemId: itemId,
        open: true,
      });
    });

    annotationPlugin.registerListener('addingAnnotationDataChanged', (event: any) => {
      if (event.detail.instanceId != instanceId || event.detail.itemId != itemId) {
        return;
      }
      onAddingAnnotationDataChanged.emit({
        act: 'onAddingAnnotationDataChanged',
        data: event.detail,
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: store.typeIdSignal(),
        itemId: itemId,
        instanceId: instanceId,
      });
    });
  }

  /**
   * Register time format changed listener
   */
  private registerTimeFormatListener(annotationPlugin: any): void {
    annotationPlugin.registerListener('timeFormatChanged', (event: any) => {
      onTimeFormatChangePlugin.emit({
        act: 'timeFormatChanged',
        format: event.detail.format,
      });
    });
  }

  /**
   * Register video-specific click listeners
   */
  private registerVideoClickListeners(annotationPlugin: any, store: MediaPlayerStore): void {
    const instanceId: string = store.instanceIdSignal();
    const itemId: string = store.itemIdSignal();

    annotationPlugin.registerListener('annotationClickedOpened', (event: any) => {
      onAnnotationClickOpened.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
      onAnnotationClickOpenedV2.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
    });

    annotationPlugin.registerListener('annotationClickedClosed', (event: any) => {
      onAnnotationClickClosed.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
      onAnnotationClickClosedV2.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
    });
  }

  /**
   * Register image and audio-specific click listeners
   */
  private registerImageAudioClickListeners(annotationPlugin: any, store: MediaPlayerStore): void {
    const instanceId: string = store.instanceIdSignal();
    const itemId: string = store.itemIdSignal();
    const typeId: string = store.typeIdSignal();

    annotationPlugin.registerListener('annotationClickedIn', (event: any) => {
      if (event.detail.instanceId != instanceId || event.detail.itemId != itemId) {
        return;
      }
      onAnnotationClickedIn.emit({
        act: 'onAnnotationClickedIn',
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: typeId,
        itemId: itemId,
        instanceId: instanceId,
        data: event.detail,
      });
      onAnnotationClickOpenedV2.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
    });

    annotationPlugin.registerListener('annotationClickedOut', (event: any) => {
      if (event.detail.instanceId != instanceId || event.detail.itemId != itemId) {
        return;
      }
      onAnnotationClickedOut.emit({
        act: 'onAnnotationClickedOut',
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: typeId,
        itemId: itemId,
        instanceId: instanceId,
        data: event.detail,
      });
      onAnnotationClickClosedV2.emit({
        annotation: event.detail.annotation,
        instanceId: instanceId,
        itemId: itemId,
      });
    });
  }

  /**
   * Register image-specific listeners
   */
  private registerImageSpecificListeners(annotationPlugin: any, store: MediaPlayerStore): void {
    const instanceId: string = store.instanceIdSignal();
    const itemId: string = store.itemIdSignal();

    annotationPlugin.registerListener('imageZoomChanged', (event: any) => {
      onImageZoomChanged.emit({
        act: 'imageZoomChanged',
        zoom: event.detail.zoom,
        viewportTransform: event.detail.viewportTransform,
        instanceId: instanceId,
        itemId: itemId,
      });
    });

    annotationPlugin.registerListener('imageViewportChanged', (event: any) => {
      onImageViewportChanged.emit({
        act: 'imageViewportChanged',
        viewportTransform: event.detail.viewportTransform,
        zoom: event.detail.zoom,
        instanceId: instanceId,
        itemId: itemId,
      });
    });
  }

  public initializeComments(mediaType: string | null, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (!store.enableAnnotationSignal()) {
      return;
    }

    store.updateStore({
      userInfo: this.authService.getAuthUser(),
      mediaType: mediaType,
      annotationsLoaded: true,
    });
  }

  public initializeImageAnnotations(instanceId: string, isConvertibleImage: boolean = false): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (store.enableAnnotationSignal()) {
      store.updateStore({
        userInfo: this.authService.getAuthUser(),
        mediaType: isConvertibleImage ? MediaType.CONVERTIBLEIMAGE : MediaType.IMAGE,
      });
    } else {
      store.updateStore({ mediaType: MediaType.IMAGE });
    }
  }

  public initializeAudioAnnotations(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (store.enableAnnotationSignal()) {
      store.updateStore({
        userInfo: this.authService.getAuthUser(),
        mediaType: MediaType.AUDIO,
      });
    } else {
      store.updateStore({ mediaType: MediaType.AUDIO });
    }
  }

  public initializeVideoAnnotations(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (store.enableAnnotationSignal()) {
      store.updateStore({
        userInfo: this.authService.getAuthUser(),
        mediaType: MediaType.VIDEO,
      });
    } else {
      store.updateStore({ mediaType: MediaType.VIDEO });
    }
  }

  public initializeAudioAnnotationsEvents(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const annotationPlugin: any = store.annotationPluginSignal();
    const isAudioPluginReady: boolean = store.isAudioPluginReadySignal();
    const mediaType: string | null = store.mediaTypeSignal();
    const itemId: string = store.itemIdSignal();
    const typeId: string = store.typeIdSignal();

    if (!isAudioPluginReady || !annotationPlugin || mediaType !== MediaType.AUDIO) {
      return;
    }

    annotationPlugin.registerListener('annotationHoveredIn', (event: any) => {
      if (event.detail.instanceId != store.instanceIdSignal() || event.detail.itemId != itemId) {
        return;
      }
      onAnnotationHoveredIn.emit({
        act: 'onAnnotationHoveredIn',
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: typeId,
        itemId: itemId,
        instanceId: store.instanceIdSignal(),
        data: event.detail,
      });
    });

    annotationPlugin.registerListener('annotationHoveredOut', (event: any) => {
      if (event.detail.instanceId != store.instanceIdSignal() || event.detail.itemId != itemId) {
        return;
      }
      onAnnotationHoveredOut.emit({
        act: 'onAnnotationHoveredOut',
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: typeId,
        itemId: itemId,
        instanceId: store.instanceIdSignal(),
        data: event.detail,
      });
    });
  }
}
